/**
 * Addresses Section — list/add/edit/delete a contact's saved addresses,
 * with a default-address star toggle. Addresses are captured automatically
 * from delivery orders (source: 'order') or added by staff (source: 'staff').
 * Structural precedent: AiInsightsPanel.tsx (single `contact` prop, `isSaving`
 * guard, toast patterns, SectionHeader styling).
 */

import { useState } from 'react'
import { MapPin, Star, Pencil, Trash2, Check, X, Plus } from 'lucide-react'
import { useCRM } from '@/hooks/useCRM'
import { usePermission } from '@/hooks/usePermission'
import { Badge, Button, Input, useToast } from '@/components/ui'
import type { CrmContactAddress, CrmContactDetail } from '@/types/crm'

interface AddressesSectionProps {
  contact: CrmContactDetail
}

export function AddressesSection({ contact }: AddressesSectionProps) {
  const { addContactAddress, updateContactAddress, deleteContactAddress } = useCRM()
  const { addToast } = useToast()
  const canWrite = usePermission('crm:write')

  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState({ rawText: '', label: '' })
  const [showAddForm, setShowAddForm] = useState(false)
  const [addDraft, setAddDraft] = useState({ rawText: '', label: '' })
  const [isSaving, setIsSaving] = useState(false)

  const addresses = contact.addresses ?? []

  const notify = (updated: CrmContactDetail | null) => {
    if (updated) {
      addToast({ type: 'success', title: 'Addresses updated' })
    } else {
      addToast({
        type: 'error',
        title: 'Failed to update addresses',
        description: 'Check your connection and try again.',
      })
    }
    return updated
  }

  const handleSetDefault = async (addressId: string) => {
    setIsSaving(true)
    try {
      notify(await updateContactAddress(contact.id, addressId, { isDefault: true }))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (addressId: string) => {
    setIsSaving(true)
    try {
      notify(await deleteContactAddress(contact.id, addressId))
    } finally {
      setIsSaving(false)
    }
  }

  const startEdit = (a: CrmContactAddress) => {
    setEditingId(a.id)
    setDraft({ rawText: a.rawText, label: a.label ?? '' })
  }

  const cancelEdit = () => setEditingId(null)

  const saveEdit = async (addressId: string) => {
    setIsSaving(true)
    try {
      const updated = notify(
        await updateContactAddress(contact.id, addressId, {
          rawText: draft.rawText,
          label: draft.label || undefined,
        }),
      )
      if (updated) setEditingId(null)
    } finally {
      setIsSaving(false)
    }
  }

  const cancelAdd = () => {
    setShowAddForm(false)
    setAddDraft({ rawText: '', label: '' })
  }

  const submitAdd = async () => {
    if (!addDraft.rawText.trim()) return
    setIsSaving(true)
    try {
      const updated = notify(
        await addContactAddress(contact.id, {
          rawText: addDraft.rawText,
          label: addDraft.label || undefined,
        }),
      )
      if (updated) cancelAdd()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-2">
      <SectionHeader title="Addresses" icon={MapPin} />

      {addresses.length === 0 ? (
        <p className="text-xs text-muted-foreground italic px-1">
          No saved addresses yet — captured automatically from delivery orders.
        </p>
      ) : (
        <div className="space-y-1">
          {addresses.map((a) => (
            <div key={a.id} className="flex items-start gap-2 px-1 py-1 rounded-lg hover:bg-muted">
              {canWrite ? (
                <button
                  type="button"
                  aria-label={a.isDefault ? 'Default address' : 'Set as default address'}
                  disabled={a.isDefault || isSaving}
                  onClick={() => handleSetDefault(a.id)}
                  className="mt-0.5 shrink-0 p-0.5 rounded-sm hover:bg-muted-foreground/10 disabled:cursor-default"
                >
                  <Star
                    className={`w-3.5 h-3.5 ${a.isDefault ? 'fill-amber-500 text-amber-500' : 'text-muted-foreground'}`}
                  />
                </button>
              ) : (
                <Star
                  aria-hidden="true"
                  className={`mt-0.5 shrink-0 w-3.5 h-3.5 ${a.isDefault ? 'fill-amber-500 text-amber-500' : 'text-muted-foreground'}`}
                />
              )}

              <div className="flex-1 min-w-0">
                {editingId === a.id ? (
                  <div className="space-y-1.5">
                    <Input
                      label="Address"
                      value={draft.rawText}
                      onChange={(e) => setDraft((d) => ({ ...d, rawText: e.target.value }))}
                    />
                    <Input
                      label="Label"
                      value={draft.label}
                      placeholder="e.g. home, work"
                      onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        type="button"
                        aria-label="Cancel editing address"
                        onClick={cancelEdit}
                        disabled={isSaving}
                        className="p-1 rounded-sm hover:bg-muted disabled:opacity-50"
                      >
                        <X className="w-4 h-4 text-muted-foreground" />
                      </button>
                      <button
                        type="button"
                        aria-label="Save address"
                        onClick={() => saveEdit(a.id)}
                        disabled={isSaving}
                        className="p-1 rounded-sm hover:bg-muted disabled:opacity-50"
                      >
                        <Check className="w-4 h-4 text-success" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm text-foreground truncate">{a.formatted ?? a.rawText}</span>
                      {a.label && <Badge variant="secondary">{a.label}</Badge>}
                    </div>
                    {a.source === 'order' && (
                      <p className="text-[10px] text-muted-foreground/75">from order</p>
                    )}
                  </>
                )}
              </div>

              {canWrite && editingId !== a.id && (
                <div className="flex gap-0.5 shrink-0">
                  <button
                    type="button"
                    aria-label="Edit address"
                    onClick={() => startEdit(a)}
                    disabled={isSaving}
                    className="p-1 rounded-sm hover:bg-muted-foreground/10 text-muted-foreground disabled:opacity-50"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Delete address"
                    onClick={() => handleDelete(a.id)}
                    disabled={isSaving}
                    className="p-1 rounded-sm hover:bg-muted-foreground/10 text-muted-foreground disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canWrite && (
        showAddForm ? (
          <div className="space-y-1.5 px-1 pt-1">
            <Input
              label="Address"
              value={addDraft.rawText}
              onChange={(e) => setAddDraft((d) => ({ ...d, rawText: e.target.value }))}
            />
            <Input
              label="Label"
              placeholder="e.g. home, work"
              value={addDraft.label}
              onChange={(e) => setAddDraft((d) => ({ ...d, label: e.target.value }))}
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                aria-label="Cancel adding address"
                onClick={cancelAdd}
                disabled={isSaving}
                className="p-1 rounded-sm hover:bg-muted disabled:opacity-50"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
              <button
                type="button"
                aria-label="Save new address"
                onClick={submitAdd}
                disabled={isSaving || !addDraft.rawText.trim()}
                className="p-1 rounded-sm hover:bg-muted disabled:opacity-50"
              >
                <Check className="w-4 h-4 text-success" />
              </button>
            </div>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAddForm(true)}
            leftIcon={<Plus className="w-3.5 h-3.5" />}
            className="font-normal text-muted-foreground"
          >
            Add address
          </Button>
        )
      )}
    </div>
  )
}

function SectionHeader({ title, icon: Icon }: { title: string; icon: React.ElementType }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 text-muted-foreground" />
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{title}</h3>
    </div>
  )
}
